// Curriculum rail — what CSOC students see in place of the Daily Leaderboard.
//
// Data is real: tournament/v1/user_webinar_detail, the same call the persona
// engine and the Meetings tab already make. That response carries two lists —
// `past_webinar_list` and `webinar_list` — but which list a row came from
// only says WHEN it's dated, not what happened to it: either list can hold a
// cancelled occurrence. Each row's own `status` (see stateOf below) is what
// actually decides completed vs cancelled vs pending.

import React, {useRef, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import Icon from 'react-native-vector-icons/FontAwesome5';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import Reveal from '@/Atoms/Reveal';
import AnimatedBar from '@/Atoms/AnimatedBar';
import SkeletonBar from '@/Atoms/SkeletonBar';
import ScrollHint from '../LeaderboardPreview/ScrollHint';
import {Webinar} from '../../personaHelpers';
import {radii} from '@/Utils/radius';

const BORDER = 'rgba(38,35,34,0.08)';
const TEAL = '#3DAB9E';
const TEAL_TINT = '#E7F4F1';
const INK = '#262322';
const MUTED = '#79736F';
const CANCEL = '#C65B4E';
const CANCEL_TINT = '#FBEAE7';

interface CurriculumPanelProps {
  /** past_webinar_list — rendered as completed. */
  past: Webinar[];
  /** webinar_list — rendered as upcoming. */
  upcoming: Webinar[];
  /**
   * True when the dashboard has stacked into one column (mobile/tablet). The
   * rail is height-capped by its parent on desktop, but stacked it has no
   * bound at all — and a student with 160+ sessions would otherwise get a
   * single card taller than the rest of the page put together.
   */
  stacked?: boolean;
}

type SessionState = 'completed' | 'cancelled' | 'pending';

interface Session {
  key: string;
  number?: number;
  topic: string;
  date: string;
  state: SessionState;
}

/**
 * Which list a row came from says almost nothing about whether the class
 * actually happened — a `past_webinar_list` entry can be a cancelled class
 * (never held) and a `webinar_list` entry can be one already cancelled ahead
 * of time. The backend already resolves this per row (webinar_util.py); read
 * ITS verdict rather than re-deriving one from list membership.
 */
const stateOf = (w: Webinar, listIsPast: boolean): SessionState => {
  if (w?.status === 'cancelled' || w?.was_cancelled) return 'cancelled';
  if (w?.status === 'completed') return 'completed';
  if (w?.status === 'scheduled' || w?.status === 'updating') return 'pending';
  // No status on this row (older/partial payload) — fall back to the list it
  // came from, the previous behaviour.
  return listIsPast ? 'completed' : 'pending';
};

const toSessions = (past: Webinar[], upcoming: Webinar[]): Session[] => {
  const map = (list: Webinar[], listIsPast: boolean) =>
    (list || []).map((w, i) => ({
      key: `${listIsPast ? 'past' : 'next'}-${w?.webinar_id ?? i}`,
      number: w?.session_number,
      topic: w?.topic || 'Session',
      date: w?.webinar_date || '',
      state: stateOf(w, listIsPast),
    }));

  const all = [...map(past, true), ...map(upcoming, false)];

  // Grouped by state first — pending (what's actually next) leads, cancelled
  // in the middle, completed history sinks to the bottom since it's the
  // least actionable of the three. DATE, not session_number, orders each
  // group: the two source lists don't share a numbering scheme (a student can
  // have a completed "Session 62" from one batch sitting alongside upcoming
  // sessions 13-16), so ordering by number interleaved them nonsensically.
  // session_number only breaks ties within a day.
  const groupOrder: Record<SessionState, number> = {
    pending: 0,
    cancelled: 1,
    completed: 2,
  };
  return all.sort((a, b) => {
    const byGroup = groupOrder[a.state] - groupOrder[b.state];
    if (byGroup !== 0) {
      return byGroup;
    }
    const byDate = String(a.date).localeCompare(String(b.date));
    if (byDate !== 0) {
      return byDate;
    }
    return (a.number ?? 0) - (b.number ?? 0);
  });
};

const formatDate = (value: string) => {
  if (!value) {
    return '';
  }
  // webinar_date is a plain YYYY-MM-DD. Split it rather than `new Date(...)`:
  // that parses a bare date as UTC and can render the previous day east of
  // Greenwich — the same trap the persona date handling hit.
  const [y, m, d] = String(value).split('-').map(Number);
  if (!y || !m || !d) {
    return String(value);
  }
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });
};

/**
 * Neutral placeholder for the rail while the persona is still resolving.
 *
 * Which panel belongs here depends on an async webinar fetch. Rendering the
 * leaderboard meanwhile meant CSOC students watched it load, settle, and then
 * get replaced by the curriculum a second later. This commits to the card
 * shape — which both panels share — but to none of the copy.
 */
export function RailSkeleton() {
  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <View style={styles.headerIcon} />
        <View style={{flex: 1, minWidth: 0, gap: 6}}>
          <SkeletonBar width="55%" height={16} />
          <SkeletonBar width="80%" height={12} />
        </View>
      </View>
      <View style={{marginTop: 16, gap: 12}}>
        {[0, 1, 2, 3, 4].map(i => (
          <SkeletonBar key={i} width="100%" height={44} />
        ))}
      </View>
    </View>
  );
}

export default function CurriculumPanel({
  past,
  upcoming,
  stacked,
}: CurriculumPanelProps) {
  const sessions = toSessions(past, upcoming);
  const completed = sessions.filter(s => s.state === 'completed').length;
  const cancelled = sessions.filter(s => s.state === 'cancelled').length;
  // Cancelled sessions never happened and never will — counting them in the
  // denominator would understate progress on the ones that did run. They
  // still render in the list below (so the student can see the cancellation
  // itself), just not in this count.
  const countedTotal = sessions.length - cancelled;
  const percent = countedTotal ? Math.round((completed / countedTotal) * 100) : 0;
  // Gates the empty-state fallback below — a fully-cancelled batch still has
  // rows to show (so the student can see why), just nothing to count.
  const total = sessions.length;
  // The first session still to come — cancelled rows can't be "next" even if
  // they're chronologically the soonest date, since nothing is happening then.
  const nextKey = sessions.find(s => s.state === 'pending')?.key;

  // Sized off the panel's own width: it's a narrow rail beside the hero on
  // desktop but full-bleed when the dashboard stacks on a phone, so the
  // viewport tells us nothing useful here.
  const [width, setWidth] = useState(0);
  const compact = width > 0 && width < 340;

  const scrollRef = useRef<any>(null);
  const [contentH, setContentH] = useState(0);
  const [viewportH, setViewportH] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const overflowing = contentH - viewportH > 8;

  return (
    <View
      style={[styles.card, compact && styles.cardCompact]}
      onLayout={e => setWidth(e.nativeEvent.layout.width)}>
      <View style={styles.header}>
        <View style={styles.headerIcon}>
          <Icon name="graduation-cap" size={17} color={CCColors.Primary.Brown} />
        </View>
        <View style={{flex: 1, minWidth: 0}}>
          <CCText fontSize={compact ? 17 : 19} fontWeight="bold" style={styles.title}>
            Your curriculum
          </CCText>
          <CCText fontSize={13} fontWeight="semibold" style={styles.count}>
            {countedTotal
              ? `${completed} of ${countedTotal} sessions done`
              : total
              ? 'Sessions cancelled — check back soon'
              : 'Your sessions will appear here'}
          </CCText>
        </View>
        {countedTotal ? (
          <View style={styles.percentPill}>
            <CCText fontSize={14} fontWeight="bold" style={{color: TEAL}}>
              {percent}%
            </CCText>
          </View>
        ) : null}
      </View>

      {total ? (
        <AnimatedBar
          percent={percent}
          color={TEAL}
          trackColor={TEAL_TINT}
          height={7}
          style={styles.progress}
        />
      ) : null}

      {total ? (
        <View style={{flex: 1, minHeight: 0}}>
          <ScrollView
            ref={scrollRef}
            nestedScrollEnabled
            style={[styles.list, stacked && styles.listStacked]}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
            scrollEventThrottle={16}
            onScroll={() => setScrolled(true)}
            onLayout={e => setViewportH(e.nativeEvent.layout.height)}
            onContentSizeChange={(_w, h) => setContentH(h)}>
            {sessions.map((s, i) => {
              const isNext = s.key === nextKey;
              return (
                <Reveal key={s.key} delay={Math.min(i, 8) * 45} distance={10}>
                  <View
                    style={[
                      styles.row,
                      isNext && styles.rowNext,
                      compact && styles.rowCompact,
                    ]}>
                    <View
                      style={[
                        styles.badge,
                        s.state === 'completed' && styles.badgeDone,
                        s.state === 'cancelled' && styles.badgeCancelled,
                        isNext && styles.badgeNext,
                      ]}>
                      {s.state === 'completed' ? (
                        <Icon
                          name="check"
                          size={13}
                          color={CCColors.White}
                          solid
                        />
                      ) : s.state === 'cancelled' ? (
                        <Icon name="times" size={13} color={CCColors.White} solid />
                      ) : (
                        <CCText
                          fontSize={13}
                          fontWeight="bold"
                          style={{color: isNext ? CCColors.White : TEAL}}>
                          {s.number ?? i + 1}
                        </CCText>
                      )}
                    </View>

                    <View style={styles.copy}>
                      <View style={styles.kickerRow}>
                        <CCText fontSize={11.5} fontWeight="semibold" style={styles.kicker}>
                          {s.number != null ? `Session ${s.number}` : 'Session'}
                          {s.date ? ` · ${formatDate(s.date)}` : ''}
                        </CCText>
                        {isNext ? (
                          <View style={styles.nextChip}>
                            <CCText fontSize={9.5} fontWeight="bold" color={CCColors.White}>
                              NEXT
                            </CCText>
                          </View>
                        ) : s.state === 'cancelled' ? (
                          <View style={styles.cancelChip}>
                            <CCText fontSize={9.5} fontWeight="bold" color={CANCEL}>
                              CANCELLED
                            </CCText>
                          </View>
                        ) : null}
                      </View>
                      <CCText
                        fontSize={14}
                        fontWeight="bold"
                        showEllipses
                        style={[
                          styles.topic,
                          s.state === 'completed' && styles.topicDone,
                          s.state === 'cancelled' && styles.topicCancelled,
                        ]}>
                        {s.topic}
                      </CCText>
                    </View>

                    {/* An icon, not the word COMPLETED: at this row height the
                        label crowded the topic and wrapped on a narrow rail. */}
                    {s.state === 'completed' ? (
                      <Icon
                        name="check-circle"
                        size={17}
                        color={CCColors.Green}
                        solid
                        style={styles.stateIcon}
                      />
                    ) : s.state === 'cancelled' ? (
                      <Icon
                        name="calendar-times"
                        size={16}
                        color={CANCEL}
                        style={styles.stateIcon}
                      />
                    ) : (
                      <Icon
                        name="clock"
                        size={16}
                        color="rgba(38,35,34,0.22)"
                        style={styles.stateIcon}
                      />
                    )}
                  </View>
                </Reveal>
              );
            })}
          </ScrollView>

          {/* Same "more below" affordance as the leaderboard rail: the list is
              height-capped and its scrollbar is hidden, so without this there's
              no signal that the rest of the curriculum exists. */}
          <ScrollHint
            visible={overflowing && !scrolled}
            onPress={() =>
              scrollRef.current?.scrollTo({
                y: Math.max(0, contentH - viewportH),
                animated: true,
              })
            }
          />
        </View>
      ) : (
        <View style={styles.empty}>
          <Icon name="calendar-alt" size={24} color="#8D8884" />
          <CCText fontSize={13} style={styles.emptyText}>
            Your curriculum will appear here once your classes are scheduled.
          </CCText>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    flex: 1,
    minHeight: 0,
    backgroundColor: CCColors.White,
    borderRadius: radii.panel,
    padding: 20,
    // Vertical only: the rail COLUMN carries the 8px horizontal inset, so a
    // horizontal margin here would push this card 8px further in than the
    // sections beside it.
    marginVertical: 8,
    alignSelf: 'stretch',
    flexShrink: 1,
    minWidth: 0,
    shadowColor: CCColors.Primary.Dark,
    shadowRadius: 8,
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.05,
    overflow: 'hidden',
  },
  cardCompact: {
    padding: 14,
    borderRadius: radii.panel,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: CCColors.Primary.lightBrown,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  title: {color: INK},
  count: {color: MUTED, marginTop: 2},
  percentPill: {
    backgroundColor: TEAL_TINT,
    borderRadius: 99,
    paddingHorizontal: 10,
    paddingVertical: 5,
    flexShrink: 0,
  },
  progress: {marginTop: 14},
  list: {marginTop: 6},
  listStacked: {maxHeight: 380},
  listContent: {paddingBottom: 26},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 11,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    gap: 12,
  },
  rowCompact: {
    paddingVertical: 9,
    gap: 10,
  },
  rowNext: {
    backgroundColor: '#F4FBF9',
    borderRadius: radii.chip,
    borderBottomColor: 'transparent',
    paddingHorizontal: 8,
  },
  badge: {
    width: 34,
    height: 34,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 17,
    backgroundColor: TEAL_TINT,
  },
  badgeDone: {backgroundColor: CCColors.Green},
  badgeCancelled: {backgroundColor: CANCEL},
  badgeNext: {backgroundColor: TEAL},
  copy: {flex: 1, minWidth: 0},
  kickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  kicker: {color: MUTED},
  nextChip: {
    backgroundColor: TEAL,
    borderRadius: 5,
    paddingHorizontal: 5,
    paddingVertical: 1,
    flexShrink: 0,
  },
  cancelChip: {
    backgroundColor: CANCEL_TINT,
    borderRadius: 5,
    paddingHorizontal: 5,
    paddingVertical: 1,
    flexShrink: 0,
  },
  topic: {color: INK, marginTop: 2},
  topicDone: {color: MUTED},
  // Muted, not strikethrough — a cancelled class often gets rescheduled to a
  // new row entirely, and a struck-through title reads as "permanently gone"
  // even when a makeup session already exists elsewhere in the list.
  topicCancelled: {color: CANCEL},
  stateIcon: {flexShrink: 0},
  empty: {
    minHeight: 170,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
  },
  emptyText: {
    color: '#8D8884',
    textAlign: 'center',
    marginTop: 10,
    lineHeight: 19,
  },
});
